import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import get from "lodash/get";

import { isNullish } from "@/helpers/common";
import { convertToArray } from "@/helpers/common";
import {
  BUSINESS_TAX_RETURN_FIELD_IDS,
  PERSONAL_TAX_RETURN_FIELD_IDS,
  SCHEDULE_C_FIELD_IDS,
  SCHEDULE_F_FIELD_IDS,
  WORKFLOW_BLOCKS_IDS,
} from "@/helpers/constants/workflow";
import {
  formatDateCustom,
  formatMoney,
  formatPayloadWithCurrency,
} from "@/helpers/formatting";
import { dispatchAction } from "@/helpers/vee-validate";
import { sortOwnersArray } from "@/helpers/workflow";
import { usePromiseWrapper } from "@/hooks/common";
import { useDeals } from "@/hooks/deals";
import { useWorkflowFields } from "@/hooks/workflow";

import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import LfCurrency from "@/components/ui/inputs/LfCurrency.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import OtherDeductionsDetail from "@/views/deals/components/OtherDeductionsDetail.vue";

import type {
  TaxReturnFieldDetail,
  TaxReturnOwnerMeta,
} from "@/models/taxReturns";
import type {
  BusinessTaxReturnBlock,
  PersonalTaxReturnBlock,
  TaxReturnDependent,
} from "@/models/workflows";
import type { OwnerWFL } from "@/models/workflows";
import type { SubmissionContext } from "vee-validate";

const DIVIDER = "divider" as const;

export const usePersonalTaxReturn = () => {
  const FIELD_PREFIX = "personal_tax_return_information_" as const;
  const { t } = useI18n();
  const { fieldData, isFieldVisible } = useWorkflowFields(
    WORKFLOW_BLOCKS_IDS.personal_tax_return
  );
  const { fieldData: ownerData } = useWorkflowFields(
    WORKFLOW_BLOCKS_IDS.personal_info
  );
  const { dispatch } = useStore();
  const { activeDeal } = useDeals();

  const yesNoFields = ["child_tax_credit", "credit_for_other_dependents"];

  const activeTab = ref<string | null>(null);

  const selectedId = ref<OwnerWFL["personal_information_id"] | null>(
    ownerData.value?.[0]?.personal_information_id || null
  );

  const currentOwner = computed<OwnerWFL | undefined>(
    () =>
      ownerData.value?.find(
        ({ personal_information_id }) =>
          personal_information_id === selectedId.value
      ) || undefined
  );

  const owners = computed(() => sortOwnersArray(ownerData.value || []));
  const activeOwnerData = computed(() => {
    return convertToArray(fieldData.value).filter(
      (record) => record.personal_information_id === selectedId.value
    );
  });

  const activeData = computed<PersonalTaxReturnBlock | undefined>(() =>
    activeTab.value !== null
      ? activeOwnerData.value?.find(
          (data) =>
            data?.personal_tax_return_information_year === activeTab.value
        )
      : undefined
  );

  const years = computed(() =>
    activeOwnerData.value?.reduce((prev, curr) => {
      if (curr.personal_tax_return_information_year) {
        prev.push(curr.personal_tax_return_information_year);
      }
      return prev;
    }, [] as string[])
  );

  const META_DETAILS = computed<TaxReturnFieldDetail[]>(() => [
    {
      name: PERSONAL_TAX_RETURN_FIELD_IDS.year,
      placeholder: t("TAX_RETURN.TAX_RETURN_YEAR"),
      value: getFieldData("year"),
      component: LfInput,
    },
    {
      name: DIVIDER,
      placeholder: "",
      value: "",
    },
  ]);

  const SUBJECT_DETAILS = computed<TaxReturnFieldDetail[]>(() =>
    [
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.taxpayer_first_name,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.TAX_PAYER_FIRST_NAME"
        ),
        value: getFieldData("taxpayer_first_name"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.taxpayer_last_name,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.TAX_PAYER_LAST_NAME"
        ),
        value: getFieldData("taxpayer_last_name"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.taxpayer_ssn,
        placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.TAX_PAYER_SSN"),
        value: getFieldData("taxpayer_ssn"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.taxpayer_address_street,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.TAX_PAYER_STREET"
        ),
        value: getFieldData("taxpayer_address_street"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.taxpayer_address_city,
        placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.TAX_PAYER_CITY"),
        value: getFieldData("taxpayer_address_city"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.taxpayer_address_state,
        placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.TAX_PAYER_STATE"),
        value: getFieldData("taxpayer_address_state"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.taxpayer_address_zip_code,
        placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.TAX_PAYER_ZIP"),
        value: getFieldData("taxpayer_address_zip_code"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.spouse_first_name,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.SPOUSE_FIRST_NAME"
        ),
        value: getFieldData("spouse_first_name"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.spouse_last_name,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.SPOUSE_LAST_NAME"
        ),
        value: getFieldData("spouse_last_name"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.spouse_ssn,
        placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.SPOUSE_SSN"),
        value: getFieldData("spouse_ssn"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.filing_status,
        placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.FILING_STATUS"),
        value: getFieldData("filing_status"),
        component: LfInput,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.taxpayer_signature_date,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.TAXPAYER_SIGNATURE_DATE"
        ),
        value: getFieldData("taxpayer_signature_date", "date"),
        component: LfInput,
        type: "date",
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.spouse_signature_date,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.SPOUSE_SIGNATURE_DATE"
        ),
        value: getFieldData("spouse_signature_date", "date"),
        component: LfInput,
        type: "date",
      },
    ].filter((record) => isFieldVisible(record.name) || record.name === DIVIDER)
  );

  const DEPENDENT_DETAILS = computed<TaxReturnFieldDetail[]>(() => {
    if (!isFieldVisible("personal_tax_return_information_dependents")) {
      return [];
    }
    const dependents =
      (getFieldData("dependents") as TaxReturnDependent[]) || [];
    return dependents.reduce((acc, dependent, index) => {
      const entries = Object.entries(dependent).map(([key, value]) => {
        return {
          name: `${PERSONAL_TAX_RETURN_FIELD_IDS.dependents}[${index}].${key}`,
          placeholder: t(
            `DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.DEPENDENT.${key.toUpperCase()}`
          ),
          displayValue: formatValue(key, value),
          value,
          component: yesNoFields.includes(key) ? LfCheckbox : LfInput,
          modelValue: value,
        };
      });
      if (entries.length) {
        acc.push({ name: DIVIDER }, ...entries);
      }
      return acc;
    }, [] as TaxReturnFieldDetail[]);
  });

  const FIELDS_DETAILS = computed<TaxReturnFieldDetail[]>(() =>
    [
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_wages_total,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_WAGES_TOTAL"
        ),
        value: getFieldData("income_wages_total"),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_taxable_interest,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_TAXABLE_INTEREST"
        ),
        value: formatMoney(getFieldData("income_taxable_interest")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_ordinary_dividends,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_ORDINARY_DIVIDENDS"
        ),
        value: getFieldData("income_ordinary_dividends"),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_qualified_dividends,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_QUALIFIED_DIVIDENDS"
        ),
        value: getFieldData("income_qualified_dividends"),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_taxable_ira_distributions,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_TAXABLE_IRA_DISTRIBUTIONS"
        ),
        value: formatMoney(getFieldData("income_taxable_ira_distributions")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_taxable_pensions_annuities,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_TAXABLE_PENSIONS_ANNUITIES"
        ),
        value: formatMoney(getFieldData("income_taxable_pensions_annuities")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_taxable_social_security,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_TAXABLE_SOCIAL_SECURITY"
        ),
        value: formatMoney(getFieldData("income_taxable_social_security")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_capital_gain_loss,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_CAPITAL_GAIN_LOSS"
        ),
        value: formatMoney(getFieldData("income_capital_gain_loss")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_other_income_loss,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_OTHER_INCOME_LOSS"
        ),
        value: formatMoney(getFieldData("income_other_income_loss")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_total_income,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_TOTAL_INCOME"
        ),
        value: formatMoney(getFieldData("income_total_income")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_adjusted_gross_income,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_ADJUSTED_GROSS_INCOME"
        ),
        value: formatMoney(getFieldData("income_adjusted_gross_income")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.income_standard_deduction,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.INCOME_STANDARD_DEDUCTION"
        ),
        value: formatMoney(getFieldData("income_standard_deduction")),
        component: LfCurrency,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.tax_and_credits_child_tax_credit,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.TAX_AND_CREDITS_CHILD_TAX_CREDIT"
        ),
        value: getFieldData("tax_and_credits_child_tax_credit")
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getFieldData("tax_and_credits_child_tax_credit"),
        component: LfCheckbox,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.tax_and_credits_self_employment_tax,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.TAX_AND_CREDITS_SELF_EMPLOYMENT_TAX"
        ),
        value: formatMoney(getFieldData("tax_and_credits_self_employment_tax")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.tax_and_credits_total_tax,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.TAX_AND_CREDITS_TOTAL_TAX"
        ),
        value: formatMoney(getFieldData("tax_and_credits_total_tax")),
        component: LfCurrency,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.payments_federal_tax_withheld_w2,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAYMENTS_FEDERAL_TAX_WITHHELD_W2"
        ),
        value: formatMoney(getFieldData("payments_federal_tax_withheld_w2")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.payments_federal_tax_withheld_1099,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAYMENTS_FEDERAL_TAX_WITHHELD_1099"
        ),
        value: formatMoney(getFieldData("payments_federal_tax_withheld_1099")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.payments_federal_tax_withheld_other,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAYMENTS_FEDERAL_TAX_WITHHELD_OTHER"
        ),
        value: formatMoney(getFieldData("payments_federal_tax_withheld_other")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.payments_estimated_tax_payments,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAYMENTS_ESTIMATED_TAX_PAYMENTS"
        ),
        value: formatMoney(getFieldData("payments_estimated_tax_payments")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.payments_earned_income_credit,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAYMENTS_EARNED_INCOME_CREDIT"
        ),
        value: formatMoney(getFieldData("payments_earned_income_credit")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.payments_additional_child_tax_credit,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAYMENTS_ADDITIONAL_CHILD_TAX_CREDIT"
        ),
        value: formatMoney(
          getFieldData("payments_additional_child_tax_credit")
        ),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.payments_american_opportunity_credit,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAYMENTS_AMERICAN_OPPORTUNITY_CREDIT"
        ),
        value: formatMoney(
          getFieldData("payments_american_opportunity_credit")
        ),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.payments_total_payments,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAYMENTS_TOTAL_PAYMENTS"
        ),
        value: formatMoney(getFieldData("payments_total_payments")),
        component: LfCurrency,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.refund_amount,
        placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.REFUND_AMOUNT"),
        value: formatMoney(getFieldData("refund_amount")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.amount_owed,
        placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.AMOUNT_OWED"),
        value: formatMoney(getFieldData("amount_owed")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.estimated_tax_penalty,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.ESTIMATED_TAX_PENALTY"
        ),
        value: formatMoney(getFieldData("estimated_tax_penalty")),
        component: LfCurrency,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.refund_amount_applied_to_next_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.REFUND_AMOUNT_APPLIED_TO_NEXT_YEAR"
        ),
        value: formatMoney(getFieldData("refund_amount_applied_to_next_year")),
        component: LfCurrency,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.third_party_designee_name,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.THIRD_PARTY_DESIGNEE_NAME"
        ),
        value: getFieldData("third_party_designee_name"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.third_party_designee_phone,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.THIRD_PARTY_DESIGNEE_PHONE"
        ),
        value: getFieldData("third_party_designee_phone"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.third_party_designee_pin,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.THIRD_PARTY_DESIGNEE_PIN"
        ),
        value: getFieldData("third_party_designee_pin"),
        component: LfInput,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.paid_preparer_name,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAID_PREPARER_NAME"
        ),
        value: getFieldData("paid_preparer_name"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.paid_preparer_ptin,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAID_PREPARER_PTIN"
        ),
        value: getFieldData("paid_preparer_ptin"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.paid_preparer_self_employed,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAID_PREPARER_SELF_EMPLOYED"
        ),
        value: getFieldData("paid_preparer_self_employed")
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getFieldData("paid_preparer_self_employed"),
        component: LfCheckbox,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.paid_preparer_firm_name,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAID_PREPARER_FIRM_NAME"
        ),
        value: getFieldData("paid_preparer_firm_name"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.paid_preparer_firm_ein,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAID_PREPARER_FIRM_EIN"
        ),
        value: getFieldData("paid_preparer_firm_ein"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.paid_preparer_firm_address_street,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAID_PREPARER_FIRM_ADDRESS_STREET"
        ),
        value: getFieldData("paid_preparer_firm_address_street"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.paid_preparer_firm_address_city,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAID_PREPARER_FIRM_ADDRESS_CITY"
        ),
        value: getFieldData("paid_preparer_firm_address_city"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.paid_preparer_firm_address_state,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAID_PREPARER_FIRM_ADDRESS_STATE"
        ),
        value: getFieldData("paid_preparer_firm_address_state"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.paid_preparer_firm_address_zip_code,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAID_PREPARER_FIRM_ADDRESS_ZIP_CODE"
        ),
        value: getFieldData("paid_preparer_firm_address_zip_code"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.paid_preparer_phone_number,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAID_PREPARER_PHONE_NUMBER"
        ),
        value: getFieldData("paid_preparer_phone_number"),
        component: LfInput,
      },
      {
        name: PERSONAL_TAX_RETURN_FIELD_IDS.paid_preparer_signature_date,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.PAID_PREPARER_SIGNATURE_DATE"
        ),
        value: getFieldData("paid_preparer_signature_date", "date"),
        component: LfInput,
        type: "date",
      },
    ].filter((record) => isFieldVisible(record.name) || record.name === DIVIDER)
  );

  const getScheduleCFieldData = (field: string, type: string = "string") => {
    const value = get(activeData.value, field);
    return type === "date" ? formatDateCustom(value, "yyyy-MM-dd") : value;
  };

  const getScheduleFFieldData = (field: string, type: string = "string") => {
    const value = get(activeData.value, field);
    return type === "date" ? formatDateCustom(value, "yyyy-MM-dd") : value;
  };

  const SCHEDULE_C_DETAILS = computed<TaxReturnFieldDetail[]>(() =>
    [
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_accounting_method,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_ACCOUNTING_METHOD"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.basic_info_accounting_method
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_business_name,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_BUSINESS_NAME"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.basic_info_business_name
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_ein,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_EIN"
        ),
        value: getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.basic_info_ein),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_ssn,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_SSN"
        ),
        value: getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.basic_info_ssn),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_proprietor_material_participant,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_PROPRIETOR_MATERIAL_PARTICIPANT"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.basic_info_proprietor_material_participant
        )
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.basic_info_proprietor_material_participant
        ),
        component: LfCheckbox,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_business_address_street,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_BUSINESS_ADDRESS_STREET"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.basic_info_business_address_street
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_business_address_city,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_BUSINESS_ADDRESS_CITY"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.basic_info_business_address_city
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_business_address_state,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_BUSINESS_ADDRESS_STATE"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.basic_info_business_address_state
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_business_address_zip_code,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_BUSINESS_ADDRESS_ZIP_CODE"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.basic_info_business_address_zip_code
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_naics_code,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_NAICS_CODE"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.basic_info_naics_code
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_profession,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_PROFESSION"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.basic_info_profession
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.basic_info_proprietor,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_BASIC_INFO_PROPRIETOR"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.basic_info_proprietor
        ),
        component: LfInput,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_car_and_truck_expenses,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_CAR_AND_TRUCK_EXPENSES"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_car_and_truck_expenses
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_interest_other,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_INTEREST_OTHER"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.expenses_interest_other)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_legal_and_professional_services,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_LEGAL_AND_PROFESSIONAL_SERVICES"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_legal_and_professional_services
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_office_expense,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_OFFICE_EXPENSE"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.expenses_office_expense)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_other_expenses,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_OTHER_EXPENSES"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.expenses_other_expenses)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_rent_or_lease_other_business_property,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_RENT_OR_LEASE_OTHER_BUSINESS_PROPERTY"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_rent_or_lease_other_business_property
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_supplies,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_SUPPLIES"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.expenses_supplies)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_taxes_and_licenses,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_TAXES_AND_LICENSES"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_taxes_and_licenses
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_total_expenses,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_TOTAL_EXPENSES"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.expenses_total_expenses)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_net_profit_or_loss,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_NET_PROFIT_OR_LOSS"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_net_profit_or_loss
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_employee_benefit_programs,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_EMPLOYEE_BENEFIT_PROGRAMS"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_employee_benefit_programs
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_energy_efficient_commercial_bldgs_deduction,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_ENERGY_EFFICIENT_COMMERCIAL_BLDGS_DEDUCTION"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_energy_efficient_commercial_bldgs_deduction
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_expenses_for_business_use_of_home,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_EXPENSES_FOR_BUSINESS_USE_OF_HOME"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_expenses_for_business_use_of_home
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_insurance_other_than_health,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_INSURANCE_OTHER_THAN_HEALTH"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_insurance_other_than_health
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_interest_mortgage,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_INTEREST_MORTGAGE"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.expenses_interest_mortgage)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_pension_and_profit_sharing_plans,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_PENSION_AND_PROFIT_SHARING_PLANS"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_pension_and_profit_sharing_plans
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_rent_or_lease_vehicles_machinery_equipment,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_RENT_OR_LEASE_VEHICLES_MACHINERY_EQUIPMENT"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_rent_or_lease_vehicles_machinery_equipment
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_repairs_and_maintenance,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_REPAIRS_AND_MAINTENANCE"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_repairs_and_maintenance
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_risk_investment_all_investment_is_at_risk,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_RISK_INVESTMENT_ALL_INVESTMENT_IS_AT_RISK"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.expenses_risk_investment_all_investment_is_at_risk
        )
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.expenses_risk_investment_all_investment_is_at_risk
        ),
        component: LfCheckbox,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_risk_investment_some_investment_is_at_risk,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_RISK_INVESTMENT_SOME_INVESTMENT_IS_AT_RISK"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.expenses_risk_investment_some_investment_is_at_risk
        )
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.expenses_risk_investment_some_investment_is_at_risk
        ),
        component: LfCheckbox,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_tentative_profits_or_loss,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_TENTATIVE_PROFITS_OR_LOSS"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_tentative_profits_or_loss
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_travel_meals_deductible_meals,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_TRAVEL_MEALS_DEDUCTIBLE_MEALS"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_travel_meals_deductible_meals
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_travel_meals_travel,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_TRAVEL_MEALS_TRAVEL"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_travel_meals_travel
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_utilities,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_UTILITIES"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.expenses_utilities)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_wages,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_WAGES"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.expenses_wages)
        ),
        component: LfCurrency,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: SCHEDULE_C_FIELD_IDS.income_gross_receipts,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_INCOME_GROSS_RECEIPTS"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.income_gross_receipts)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.income_gross_income,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_INCOME_GROSS_INCOME"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.income_gross_income)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.income_gross_profit,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_INCOME_GROSS_PROFIT"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.income_gross_profit)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.income_balance,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_INCOME_BALANCE"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.income_balance)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.income_cost_of_goods_sold,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_INCOME_COST_OF_GOODS_SOLD"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.income_cost_of_goods_sold)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.income_other_income_loss,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_INCOME_OTHER_INCOME_LOSS"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.income_other_income_loss)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.income_returns_and_allowances,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_INCOME_RETURNS_AND_ALLOWANCES"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.income_returns_and_allowances
          )
        ),
        component: LfCurrency,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: SCHEDULE_C_FIELD_IDS.cogs_closing_inventory_method,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_COGS_CLOSING_INVENTORY_METHOD"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.cogs_closing_inventory_method
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.cogs_cost_of_goods_sold,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_COGS_COST_OF_GOODS_SOLD"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.cogs_cost_of_goods_sold)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.cogs_cost_of_labor,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_COGS_COST_OF_LABOR"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.cogs_cost_of_labor)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.cogs_costs_calculated,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_COGS_COSTS_CALCULATED"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.cogs_costs_calculated)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.cogs_inventory_at_beginning_of_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_COGS_INVENTORY_AT_BEGINNING_OF_YEAR"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.cogs_inventory_at_beginning_of_year
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.cogs_inventory_end_of_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_COGS_INVENTORY_END_OF_YEAR"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.cogs_inventory_end_of_year)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.cogs_materials_and_supplies,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_COGS_MATERIALS_AND_SUPPLIES"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.cogs_materials_and_supplies
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.cogs_other_costs,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_COGS_OTHER_COSTS"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.cogs_other_costs)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.cogs_purchases,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_COGS_PURCHASES"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.cogs_purchases)
        ),
        component: LfCurrency,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: SCHEDULE_C_FIELD_IDS.vehicle_info_another_vehicle_available_for_personal_use,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_VEHICLE_INFO_ANOTHER_VEHICLE_AVAILABLE_FOR_PERSONAL_USE"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_another_vehicle_available_for_personal_use
        )
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_another_vehicle_available_for_personal_use
        ),
        component: LfCheckbox,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.vehicle_info_evidence_for_deduction_available,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_VEHICLE_INFO_EVIDENCE_FOR_DEDUCTION_AVAILABLE"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_evidence_for_deduction_available
        )
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_evidence_for_deduction_available
        ),
        component: LfCheckbox,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.vehicle_info_evidence_is_written,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_VEHICLE_INFO_EVIDENCE_IS_WRITTEN"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_evidence_is_written
        )
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_evidence_is_written
        ),
        component: LfCheckbox,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.vehicle_info_vehicle_mileage_for_business,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_VEHICLE_INFO_VEHICLE_MILEAGE_FOR_BUSINESS"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_vehicle_mileage_for_business
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.vehicle_info_vehicle_mileage_for_commuting,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_VEHICLE_INFO_VEHICLE_MILEAGE_FOR_COMMUTING"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_vehicle_mileage_for_commuting
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.vehicle_info_vehicle_mileage_for_other,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_VEHICLE_INFO_VEHICLE_MILEAGE_FOR_OTHER"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_vehicle_mileage_for_other
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.vehicle_info_vehicle_put_in_service_date,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_VEHICLE_INFO_VEHICLE_PUT_IN_SERVICE_DATE"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_vehicle_put_in_service_date,
          "date"
        ),
        component: LfInput,
        type: "date",
      },
      {
        name: SCHEDULE_C_FIELD_IDS.vehicle_info_vehicle_was_available_for_personal_use,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_VEHICLE_INFO_VEHICLE_WAS_AVAILABLE_FOR_PERSONAL_USE"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_vehicle_was_available_for_personal_use
        )
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.vehicle_info_vehicle_was_available_for_personal_use
        ),
        component: LfCheckbox,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: SCHEDULE_C_FIELD_IDS.other_expenses_details,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_OTHER_EXPENSES_DETAILS"
        ),
        value: getScheduleCFieldData(
          SCHEDULE_C_FIELD_IDS.other_expenses_details
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_advertising,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_ADVERTISING"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.expenses_advertising)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_comissions_and_fees,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_COMISSIONS_AND_FEES"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_comissions_and_fees
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_contract_labor,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_CONTRACT_LABOR"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.expenses_contract_labor)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_depletion,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_DEPLETION"
        ),
        value: formatMoney(
          getScheduleCFieldData(SCHEDULE_C_FIELD_IDS.expenses_depletion)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_C_FIELD_IDS.expenses_depreciation_and_expense_deduction,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.C_EXPENSES_DEPRECIATION_AND_EXPENSE_DEDUCTION"
        ),
        value: formatMoney(
          getScheduleCFieldData(
            SCHEDULE_C_FIELD_IDS.expenses_depreciation_and_expense_deduction
          )
        ),
        component: LfCurrency,
      },
    ].filter((record) => isFieldVisible(record.name) || record.name === DIVIDER)
  );

  const SCHEDULE_F_DETAILS = computed<TaxReturnFieldDetail[]>(() =>
    [
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: SCHEDULE_F_FIELD_IDS.basic_info_accounting_method,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_BASIC_INFO_ACCOUNTING_METHOD"
        ),
        value: getScheduleFFieldData(
          SCHEDULE_F_FIELD_IDS.basic_info_accounting_method
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.basic_info_activity,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_BASIC_INFO_ACTIVITY"
        ),
        value: getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.basic_info_activity),
        component: LfInput,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.basic_info_ein,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_BASIC_INFO_EIN"
        ),
        value: getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.basic_info_ein),
        component: LfInput,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.basic_info_ssn,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_BASIC_INFO_SSN"
        ),
        value: getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.basic_info_ssn),
        component: LfInput,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.basic_info_proprietor_name,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_BASIC_INFO_PROPRIETOR_NAME"
        ),
        value: getScheduleFFieldData(
          SCHEDULE_F_FIELD_IDS.basic_info_proprietor_name
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.basic_info_materially_participated_in_business,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_BASIC_INFO_MATERIALLY_PARTICIPATED_IN_BUSINESS"
        ),
        value: getScheduleFFieldData(
          SCHEDULE_F_FIELD_IDS.basic_info_materially_participated_in_business
        )
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getScheduleFFieldData(
          SCHEDULE_F_FIELD_IDS.basic_info_materially_participated_in_business
        ),
        component: LfCheckbox,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_gross_income,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_GROSS_INCOME"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.cash_gross_income)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_purchased_livestock_and_other_items_profit,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_PURCHASED_LIVESTOCK_AND_OTHER_ITEMS_PROFIT"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_purchased_livestock_and_other_items_profit
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_purchased_livestock_and_other_items_sales,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_PURCHASED_LIVESTOCK_AND_OTHER_ITEMS_SALES"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_purchased_livestock_and_other_items_sales
          )
        ),
        component: LfCurrency,
      },
      {
        name: DIVIDER,
        placeholder: "",
        value: "",
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_depreciation_expenses,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_DEPRECIATION_EXPENSES"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_depreciation_expenses
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_feed,
        placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_FEED"),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_feed)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_interest_mortgage,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_INTEREST_MORTGAGE"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_interest_mortgage)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_interest_other,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_INTEREST_OTHER"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_interest_other)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_total_expenses,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_TOTAL_EXPENSES"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_total_expenses)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_net_farm_profit_or_loss,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_NET_FARM_PROFIT_OR_LOSS"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_net_farm_profit_or_loss
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.basic_info_made_payments_that_require_1099,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_BASIC_INFO_MADE_PAYMENTS_THAT_REQUIRE_1099"
        ),
        value: getScheduleFFieldData(
          SCHEDULE_F_FIELD_IDS.basic_info_made_payments_that_require_1099
        )
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getScheduleFFieldData(
          SCHEDULE_F_FIELD_IDS.basic_info_made_payments_that_require_1099
        ),
        component: LfCheckbox,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.basic_info_part_iv_code,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_BASIC_INFO_PART_IV_CODE"
        ),
        value: getScheduleFFieldData(
          SCHEDULE_F_FIELD_IDS.basic_info_part_iv_code
        ),
        component: LfInput,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.basic_info_will_file_1099s,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_BASIC_INFO_WILL_FILE_1099S"
        ),
        value: getScheduleFFieldData(
          SCHEDULE_F_FIELD_IDS.basic_info_will_file_1099s
        )
          ? t("COMMON.YES")
          : t("COMMON.NO"),
        modelValue: getScheduleFFieldData(
          SCHEDULE_F_FIELD_IDS.basic_info_will_file_1099s
        ),
        component: LfCheckbox,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_agricultural_program_payments,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_AGRICULTURAL_PROGRAM_PAYMENTS"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_agricultural_program_payments
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_agricultural_program_payments_taxable_amount,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_AGRICULTURAL_PROGRAM_PAYMENTS_TAXABLE_AMOUNT"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_agricultural_program_payments_taxable_amount
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_ccc_loans_forfeited,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_CCC_LOANS_FORFEITED"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.cash_ccc_loans_forfeited)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_ccc_loans_forfeited_taxable_amount,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_CCC_LOANS_FORFEITED_TAXABLE_AMOUNT"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_ccc_loans_forfeited_taxable_amount
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_ccc_loans_reported,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_CCC_LOANS_REPORTED"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.cash_ccc_loans_reported)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_cooperative_distributions,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_COOPERATIVE_DISTRIBUTIONS"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_cooperative_distributions
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_cooperative_distributions_taxable_amount,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_COOPERATIVE_DISTRIBUTIONS_TAXABLE_AMOUNT"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_cooperative_distributions_taxable_amount
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_crop_deferred_from_prior_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_CROP_DEFERRED_FROM_PRIOR_YEAR"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_crop_deferred_from_prior_year
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_crop_insurance_proceeds_amount_received,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_CROP_INSURANCE_PROCEEDS_AMOUNT_RECEIVED"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_crop_insurance_proceeds_amount_received
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_and_federal_crop_disaster_payments,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_AND_FEDERAL_CROP_DISASTER_PAYMENTS"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_and_federal_crop_disaster_payments
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_crop_insurance_proceeds_defer_to_next_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_CROP_INSURANCE_PROCEEDS_DEFER_TO_NEXT_YEAR"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_crop_insurance_proceeds_defer_to_next_year
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_crop_insurance_proceeds_taxable_amount,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_CROP_INSURANCE_PROCEEDS_TAXABLE_AMOUNT"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.cash_crop_insurance_proceeds_taxable_amount
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_custom_hire_income,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_CUSTOM_HIRE_INCOME"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.cash_custom_hire_income)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_other_income,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_OTHER_INCOME"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.cash_other_income)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_and_other_items_sales,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_AND_OTHER_ITEMS_SALES"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.cash_and_other_items_sales)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.cash_sales_of_products,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_CASH_SALES_OF_PRODUCTS"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.cash_sales_of_products)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_activity_investment,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_ACTIVITY_INVESTMENT"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_activity_investment
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_car_and_truck_expenses,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_CAR_AND_TRUCK_EXPENSES"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_car_and_truck_expenses
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_chemicals,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_CHEMICALS"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_chemicals)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_conservation_expenses,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_CONSERVATION_EXPENSES"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_conservation_expenses
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_custom_hire,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_CUSTOM_HIRE"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_custom_hire)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_employee_benefit_programs,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_EMPLOYEE_BENEFIT_PROGRAMS"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_employee_benefit_programs
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_fertilizers_and_line,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_FERTILIZERS_AND_LINE"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_fertilizers_and_line
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_freight_and_trucking,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_FREIGHT_AND_TRUCKING"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_freight_and_trucking
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_gas_fuel_oil,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_GAS_FUEL_OIL"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_gas_fuel_oil)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_insurance,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_INSURANCE"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_insurance)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_interest,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_INTEREST"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_interest)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_labor_hired,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_LABOR_HIRED"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_labor_hired)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_other_expenses,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_OTHER_EXPENSES"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_other_expenses)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_pension_and_profit_sharing,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_PENSION_AND_PROFIT_SHARING"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_pension_and_profit_sharing
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_rent_or_lease,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_RENT_OR_LEASE"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_rent_or_lease)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_rent_or_lease_other,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_RENT_OR_LEASE_OTHER"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_rent_or_lease_other
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_rent_machinery_equipment,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_RENT_MACHINERY_EQUIPMENT"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_rent_machinery_equipment
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_repairs_and_maintenance,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_REPAIRS_AND_MAINTENANCE"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_repairs_and_maintenance
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_seeds_and_plants,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_SEEDS_AND_PLANTS"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_seeds_and_plants)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_storage_and_warehousing,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_STORAGE_AND_WAREHOUSING"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_storage_and_warehousing
          )
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_supplies,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_SUPPLIES"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_supplies)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_taxes,
        placeholder: t("DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_TAXES"),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_taxes)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_utilities,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_UTILITIES"
        ),
        value: formatMoney(
          getScheduleFFieldData(SCHEDULE_F_FIELD_IDS.accrual_utilities)
        ),
        component: LfCurrency,
      },
      {
        name: SCHEDULE_F_FIELD_IDS.accrual_veterinary_and_medicine,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.PERSONAL_TAX_INFO.F_ACCRUAL_VETERINARY_AND_MEDICINE"
        ),
        value: formatMoney(
          getScheduleFFieldData(
            SCHEDULE_F_FIELD_IDS.accrual_veterinary_and_medicine
          )
        ),
        component: LfCurrency,
      },
    ].filter((record) => isFieldVisible(record.name) || record.name === DIVIDER)
  );

  const COMBINED_FIELDS = computed<TaxReturnFieldDetail[]>(() => [
    ...META_DETAILS.value,
    ...SUBJECT_DETAILS.value,
    ...DEPENDENT_DETAILS.value,
    ...FIELDS_DETAILS.value,
    ...SCHEDULE_C_DETAILS.value,
    ...SCHEDULE_F_DETAILS.value,
  ]);

  const getFieldData = (
    id:
      | keyof typeof PERSONAL_TAX_RETURN_FIELD_IDS
      | keyof typeof SCHEDULE_F_FIELD_IDS
      | keyof typeof SCHEDULE_C_FIELD_IDS,
    type: string = "string"
  ) => {
    const key = `${FIELD_PREFIX}${id}`;
    const value = get(activeData.value, key);
    return type === "date" ? formatDateCustom(value, "yyyy-MM-dd") : value;
  };

  const formatValue = (
    key: string,
    value: string | number | boolean
  ): string | null => {
    if (yesNoFields.includes(key)) {
      return typeof value === "boolean"
        ? value
          ? t("COMMON.YES")
          : t("COMMON.NO")
        : String(value);
    }
    return value ? String(value) : null;
  };

  const hasNoValue = (
    value: string | number | boolean | null | undefined
  ): boolean => {
    return isNullish(value) || value === "";
  };

  const setActiveTab = (year: string | null) => {
    activeTab.value = year;
  };

  const { fetchWrapper: updatePersonalTaxInformation, loading: updating } =
    usePromiseWrapper(
      async (
        values: PersonalTaxReturnBlock,
        actions: SubmissionContext<PersonalTaxReturnBlock>,
        data: TaxReturnOwnerMeta
      ) => {
        values.personal_information_id = data.personal_information_id;
        values.personal_tax_return_id =
          data.personal_tax_return_id ?? undefined;

        if (
          !hasNoValue(
            values.personal_tax_return_information_payments_total_payments
          )
        ) {
          values.personal_tax_return_information_payments_total_payments =
            Number(
              values.personal_tax_return_information_payments_total_payments
            );
        }

        values.personal_tax_return_information_tax_and_credits_child_tax_credit =
          Boolean(
            values.personal_tax_return_information_tax_and_credits_child_tax_credit
          );

        await dispatchAction(
          {
            [WORKFLOW_BLOCKS_IDS.personal_tax_return]: [
              formatPayloadWithCurrency(values),
            ],
          },
          actions,
          "workflows/updateWorkflowSnapshotFields"
        );
        await dispatch("applications/getApplication", activeDeal.value.id);
      }
    );

  const setOwner = (id: OwnerWFL["personal_information_id"]) => {
    selectedId.value = id;
  };

  const isSelected = (id: OwnerWFL["personal_information_id"]) =>
    selectedId.value === id;

  return {
    activeTab,
    activeData,
    owners,
    selectedId,
    activeOwnerData,
    currentOwner,
    fieldData,
    years,
    SUBJECT_DETAILS,
    DEPENDENT_DETAILS,
    FIELDS_DETAILS,
    COMBINED_FIELDS,
    DIVIDER,
    setActiveTab,
    updatePersonalTaxInformation,
    setOwner,
    isSelected,
    updating,
  };
};

export const useBusinessTaxReturn = () => {
  const FIELD_PREFIX = "business_tax_return_" as const;

  const { t } = useI18n();
  const { fieldData, isFieldVisible } = useWorkflowFields(
    WORKFLOW_BLOCKS_IDS.business_tax_return
  );
  const { dispatch } = useStore();
  const { activeDeal } = useDeals();

  const indexOfActiveTab = ref<number | null>(0);

  const activeData = computed<BusinessTaxReturnBlock | undefined>(() => {
    if (!fieldData.value?.length || indexOfActiveTab.value === null) {
      return;
    }

    return fieldData.value[indexOfActiveTab.value];
  });

  const years = computed(() => {
    // useWorkflowFields falls back to {} (not []) for blocks not listed in
    // MULTI_RECORD_BLOCKS, so fieldData may be an object before data loads.
    // Guard against that before calling .reduce.
    if (!Array.isArray(fieldData.value)) {
      return [];
    }
    return fieldData.value.reduce<string[]>((prev, curr) => {
      if (curr.business_tax_return_year) {
        prev.push(curr.business_tax_return_year);
      }
      return prev;
    }, []);
  });

  const META_DETAILS = computed<TaxReturnFieldDetail[]>(() => [
    {
      name: BUSINESS_TAX_RETURN_FIELD_IDS.year,
      placeholder: t("TAX_RETURN.TAX_RETURN_YEAR"),
      value: getFieldData("year"),
      component: LfInput,
    },
    {
      name: DIVIDER,
      placeholder: "",
      value: "",
    },
  ]);

  const COMPANY_DETAILS = computed<TaxReturnFieldDetail[]>(() =>
    [
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.corporation_name,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.CORPORATION_NAME"
        ),
        value: getFieldData("corporation_name"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.employer_identification_number,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.EMPLOYER_IDENTIFICATION_NUMBER"
        ),
        value: getFieldData("employer_identification_number"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.date_incorporated,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.DATE_INCORPORATED"
        ),
        value: getFieldData("date_incorporated"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.business_activity,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.BUSINESS_ACTIVITY"
        ),
        value: getFieldData("business_activity"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.current_year_allocation_percentage,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.CURRENT_YEAR_ALLOCATION_PERCENTAGE"
        ),
        value: getFieldData("current_year_allocation_percentage"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.product_or_service,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.PRODUCT_OR_SERVICE"
        ),
        value: getFieldData("product_or_service"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.shareholder_name,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SHAREHOLDER_NAME"
        ),
        value: getFieldData("shareholder_name"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.shareholder_identifying_number,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SHAREHOLDER_IDENTIFYING_NUMBER"
        ),
        value: getFieldData("shareholder_identifying_number"),
        component: LfInput,
      },
    ].filter((record) => isFieldVisible(record.name) || record.name === DIVIDER)
  );

  const FORM_8879_DETAILS = computed<TaxReturnFieldDetail[]>(() =>
    [
      {
        name: DIVIDER,
        placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_8879"),
        value: "",
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_8879_corp_tax_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_8879_CORP_TAX_YEAR"
        ),
        value: getFieldData("form_8879_corp_tax_year"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_8879_corp_total_income,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_8879_CORP_TOTAL_INCOME"
        ),
        value: getFieldData("form_8879_corp_total_income"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_8879_corp_officer_signature_date,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_8879_CORP_OFFICER_SIGNATURE_DATE"
        ),
        value: getFieldData("form_8879_corp_officer_signature_date"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_8879_corp_ero_signature_date,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_8879_CORP_ERO_SIGNATURE_DATE"
        ),
        value: getFieldData("form_8879_corp_ero_signature_date"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_8879_corp_ero_efin,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_8879_CORP_ERO_EFIN"
        ),
        value: getFieldData("form_8879_corp_ero_efin"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_8879_corp_ero_pin,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_8879_CORP_ERO_PIN"
        ),
        value: getFieldData("form_8879_corp_ero_pin"),
        component: LfInput,
      },
    ].filter((record) => isFieldVisible(record.name) || record.name === DIVIDER)
  );

  const FORM_1120S_DETAILS = computed<TaxReturnFieldDetail[]>(() =>
    [
      {
        name: DIVIDER,
        placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S"),
        value: "",
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_tax_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_TAX_YEAR"
        ),
        value: getFieldData("form_1120s_tax_year"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_selection_effective_date,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_SELECTION_EFFECTIVE_DATE"
        ),
        value: getFieldData("form_1120s_selection_effective_date"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_total_assets,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_TOTAL_ASSETS"
        ),
        value: getFieldData("form_1120s_total_assets"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_gross_receipts_or_sales,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_GROSS_RECEIPTS_OR_SALES"
        ),
        value: getFieldData("form_1120s_gross_receipts_or_sales"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_cost_of_goods_sold,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_COST_OF_GOODS_SOLD"
        ),
        value: getFieldData("form_1120s_cost_of_goods_sold"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_gross_profit,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_GROSS_PROFIT"
        ),
        value: getFieldData("form_1120s_gross_profit"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_net_gain_loss_from_form_4797,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_NET_GAIN_LOSS_FROM_FORM_4797"
        ),
        value: getFieldData("form_1120s_net_gain_loss_from_form_4797"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_total_income_loss,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_TOTAL_INCOME_LOSS"
        ),
        value: getFieldData("form_1120s_total_income_loss"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_ordinary_business_income_loss,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_ORDINARY_BUSINESS_INCOME_LOSS"
        ),
        value: getFieldData("form_1120s_ordinary_business_income_loss"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_total_deductions,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_TOTAL_DEDUCTIONS"
        ),
        value: getFieldData("form_1120s_total_deductions"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_other_deductions,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_OTHER_DEDUCTIONS"
        ),
        value: getFieldData("form_1120s_other_deductions"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_depreciation,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_DEPRECIATION"
        ),
        value: getFieldData("form_1120s_depreciation"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_rents,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_RENTS"
        ),
        value: getFieldData("form_1120s_rents"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_repairs_and_maintenance,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_REPAIRS_AND_MAINTENANCE"
        ),
        value: getFieldData("form_1120s_repairs_and_maintenance"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_advertising,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_ADVERTISING"
        ),
        value: getFieldData("form_1120s_advertising"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_total_shares_outstanding,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_TOTAL_SHARES_OUTSTANDING"
        ),
        value: getFieldData("form_1120s_total_shares_outstanding"),
        component: LfInput,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.form_1120s_accounting_method,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.FORM_1120S_ACCOUNTING_METHOD"
        ),
        value: getFieldData("form_1120s_accounting_method"),
        component: LfInput,
      },
    ].filter((record) => isFieldVisible(record.name) || record.name === DIVIDER)
  );

  const OTHER_DEDUCTIONS_DETAILS = computed<TaxReturnFieldDetail[]>(() => {
    if (
      !isFieldVisible(BUSINESS_TAX_RETURN_FIELD_IDS.other_deductions_detail)
    ) {
      return [];
    }
    const deductions = getFieldData("other_deductions_detail");

    return [
      {
        name: DIVIDER,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.OTHER_DEDUCTIONS_DETAIL"
        ),
        value: "",
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.other_deductions_detail,
        placeholder: "",
        value: deductions ?? [],
        component: OtherDeductionsDetail,
      },
    ];
  });

  const SCHEDULE_K1_DETAILS = computed<TaxReturnFieldDetail[]>(() =>
    [
      {
        name: DIVIDER,
        placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_K1"),
        value: "",
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_k1_ordinary_business_income_loss,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_K1_ORDINARY_BUSINESS_INCOME_LOSS"
        ),
        value: getFieldData("schedule_k1_ordinary_business_income_loss"),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_k1_net_section_1231_gain_loss,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_K1_NET_SECTION_1231_GAIN_LOSS"
        ),
        value: formatMoney(
          getFieldData("schedule_k1_net_section_1231_gain_loss")
        ),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_k1_post_1986_depreciation_adjustment,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_K1_POST_1986_DEPRECIATION_ADJUSTMENT"
        ),
        value: getFieldData("schedule_k1_post_1986_depreciation_adjustment"),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_k1_adjusted_gain_or_loss,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_K1_ADJUSTED_GAIN_OR_LOSS"
        ),
        value: getFieldData("schedule_k1_adjusted_gain_or_loss"),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_k1_distributions,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_K1_DISTRIBUTIONS"
        ),
        value: formatMoney(getFieldData("schedule_k1_distributions")),
        component: LfCurrency,
      },
    ].filter((record) => isFieldVisible(record.name) || record.name === DIVIDER)
  );

  const SCHEDULE_L_DETAILS = computed<TaxReturnFieldDetail[]>(() =>
    [
      {
        name: DIVIDER,
        placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_L"),
        value: "",
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_l_cash_beginning_of_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_L_CASH_BEGINNING_OF_YEAR"
        ),
        value: getFieldData("schedule_l_cash_beginning_of_year"),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_l_cash_end_of_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_L_CASH_END_OF_YEAR"
        ),
        value: getFieldData("schedule_l_cash_end_of_year"),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_l_total_assets_beginning_of_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_L_TOTAL_ASSETS_BEGINNING_OF_YEAR"
        ),
        value: formatMoney(
          getFieldData("schedule_l_total_assets_beginning_of_year")
        ),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_l_total_assets_end_of_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_L_TOTAL_ASSETS_END_OF_YEAR"
        ),
        value: getFieldData("schedule_l_total_assets_end_of_year"),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_l_accounts_payable_beginning_of_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_L_ACCOUNTS_PAYABLE_BEGINNING_OF_YEAR"
        ),
        value: getFieldData("schedule_l_accounts_payable_beginning_of_year"),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_l_accounts_payable_end_of_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_L_ACCOUNTS_PAYABLE_END_OF_YEAR"
        ),
        value: formatMoney(
          getFieldData("schedule_l_accounts_payable_end_of_year")
        ),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_l_total_liabilities_and_equity_beginning_of_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_L_TOTAL_LIABILITIES_AND_EQUITY_BEGINNING_OF_YEAR"
        ),
        value: formatMoney(
          getFieldData(
            "schedule_l_total_liabilities_and_equity_beginning_of_year"
          )
        ),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_l_total_liabilities_and_equity_end_of_year,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_L_TOTAL_LIABILITIES_AND_EQUITY_END_OF_YEAR"
        ),
        value: formatMoney(
          getFieldData("schedule_l_total_liabilities_and_equity_end_of_year")
        ),
        component: LfCurrency,
      },
    ].filter((record) => isFieldVisible(record.name) || record.name === DIVIDER)
  );

  const SCHEDULE_M1_DETAILS = computed<TaxReturnFieldDetail[]>(() =>
    [
      {
        name: DIVIDER,
        placeholder: t("DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_M1"),
        value: "",
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_m1_net_income_loss_per_books,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_M1_NET_INCOME_LOSS_PER_BOOKS"
        ),
        value: getFieldData("schedule_m1_net_income_loss_per_books"),
        component: LfCurrency,
      },
      {
        name: BUSINESS_TAX_RETURN_FIELD_IDS.schedule_m1_income_loss_schedule_k,
        placeholder: t(
          "DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.SCHEDULE_M1_INCOME_LOSS_SCHEDULE_K"
        ),
        value: getFieldData("schedule_m1_income_loss_schedule_k"),
        component: LfCurrency,
      },
    ].filter((record) => isFieldVisible(record.name) || record.name === DIVIDER)
  );

  const COMBINED_FIELDS = computed<TaxReturnFieldDetail[]>(() => [
    ...META_DETAILS.value,
    ...COMPANY_DETAILS.value,
    ...FORM_8879_DETAILS.value,
    ...FORM_1120S_DETAILS.value,
    ...SCHEDULE_K1_DETAILS.value,
    ...SCHEDULE_L_DETAILS.value,
    ...SCHEDULE_M1_DETAILS.value,
    ...OTHER_DEDUCTIONS_DETAILS.value,
  ]);

  const getFieldData = (
    id: keyof typeof BUSINESS_TAX_RETURN_FIELD_IDS,
    type: string = "string"
  ) => {
    const key = `${FIELD_PREFIX}${id}`;
    const value = get(activeData.value, key);
    return type === "date" ? formatDateCustom(value, "yyyy-MM-dd") : value;
  };

  const setActiveTabIndex = (tabIndex: number | null) => {
    indexOfActiveTab.value = tabIndex;
  };

  const { fetchWrapper: updateBusinessTaxInformation, loading: updating } =
    usePromiseWrapper(
      async (
        values: BusinessTaxReturnBlock,
        actions: SubmissionContext<BusinessTaxReturnBlock>
      ) => {
        await dispatchAction(
          {
            [WORKFLOW_BLOCKS_IDS.business_tax_return]: [
              formatPayloadWithCurrency(values),
            ],
          },
          actions,
          "workflows/updateWorkflowSnapshotFields"
        );
        await dispatch("applications/getApplication", activeDeal.value.id);
      }
    );

  return {
    indexOfActiveTab,
    activeData,
    fieldData,
    years,
    COMPANY_DETAILS,
    FORM_8879_DETAILS,
    FORM_1120S_DETAILS,
    OTHER_DEDUCTIONS_DETAILS,
    SCHEDULE_K1_DETAILS,
    SCHEDULE_L_DETAILS,
    COMBINED_FIELDS,
    DIVIDER,
    setActiveTabIndex,
    updateBusinessTaxInformation,
    updating,
  };
};
